import { AnswerTypes, Sender } from 'entities/Message'
import customAnalytics from 'utils/analytics'
import TRACKING_INFORMATION from 'utils/trackingInformation'
import { getLoginBaseUrl, getApiBaseUrl } from 'utils/url'
import { getTenant, getTenantLanguage } from 'utils/getTenantSpecificValues'
import sanitizeUserMessage from 'utils/sanitizeUserMessage'
import {
  getBackendSession,
  getUserlikeSession,
  loadSettingsItems,
} from 'utils/sessionStorage'
import dataLayerPushEvent from 'utils/dataLayerPushEvent'
import fetchJson from 'utils/fetchJson'
import UAParser from 'ua-parser-js'
import { t } from '@lingui/macro'
import type { CommunicationService } from 'entities/CommunicationService'
import type VerifyUserResponse from 'entities/VerifyUserResponse'
import type Message from 'entities/Message'
import type { UserMessage } from 'entities/Message'
import type ContextOfConversation from 'entities/ContextOfConversation'
import type { UserlikePayload } from 'entities/Userlike'

interface Request {
  endpoint: string
  payload?: object
  refreshUuid?: boolean
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  params?: object
}

const getCommunicationService = (): CommunicationService => {
  if (getUserlikeSession()) {
    return 'userlike'
  }

  return getBackendSession()
}

export const sendRequest = async (props: Request) => {
  const {
    endpoint,
    payload,
    refreshUuid = false,
    method = 'POST',
    params = {},
  } = props
  const verifyToken = process.env.REACT_APP_API_TOKEN || ''
  const tenant = getTenant()
  const language = getTenantLanguage()
  const headers: HeadersInit = {
    'Content-Type': 'application/json; charset=utf-8',
    'X-Verify-Token': verifyToken,
  }

  if (refreshUuid) {
    headers['X-Refresh-UUID'] = 'true'
  }

  const url = `${getApiBaseUrl()}/${endpoint}`

  return fetchJson(url, {
    method,
    headers,
    body: payload ? JSON.stringify(payload) : undefined,
    params: {
      tenant,
      language,
      ...params,
    },
  })
}

interface SendMessageProps {
  message: UserMessage
  messages: Array<Message>
  contexts: Array<ContextOfConversation>
  customerNumber: string | null
  conversationId?: string
  captcha?: string
}

interface SendMessagePayload extends UserMessage {
  contexts: Array<ContextOfConversation>
  service: CommunicationService
  conversationId?: string
  customerNumber: string | null
  captcha?: string
}

const handleDataLayerEvents = (
  message: UserMessage,
  messages: Array<Message>
) => {
  if (
    message.type === AnswerTypes.Text ||
    message.type === AnswerTypes.MultipleActions ||
    message.type === AnswerTypes.ResetPassword
  ) {
    customAnalytics.track(TRACKING_INFORMATION.messageSent.action, {
      category: TRACKING_INFORMATION.messageSent.category,
      label: message.type,
    })

    const isFirstUserMessage =
      messages.filter(({ sender }) => sender === Sender.User).length === 1

    if (isFirstUserMessage) {
      dataLayerPushEvent('chatbotInteracted')
    }

    const trigger = message.payload?.trigger

    if (trigger === 'feedback_pos' || trigger === 'faq_feedback_pos') {
      dataLayerPushEvent('chatbotFeedbackUsecasePositive')
    }

    if (trigger === 'feedback_neg' || trigger === 'faq_feedback_neg') {
      dataLayerPushEvent('chatbotFeedbackUsecaseNegative')
    }
  }
}

const processResponses = (responses: Array<any>) =>
  responses.map((message) => {
    const processedMessage = message
    if (processedMessage?.type === AnswerTypes.OrderList) {
      const backend = getBackendSession()
      if (backend === 'backend-1') {
        processedMessage.type = AnswerTypes.OrderListDEPRECATED
      }
    }

    return processedMessage
  })

export const sendMessage = async ({
  message,
  messages,
  contexts,
  conversationId,
  customerNumber,
  captcha,
}: SendMessageProps) => {
  const payload: SendMessagePayload = {
    ...message,
    contexts,
    service: getCommunicationService(),
    conversationId,
    customerNumber,
  }

  if (captcha) {
    payload.captcha = captcha
  }

  const data = await sendRequest({
    endpoint: 'webchat',
    payload,
  })

  handleDataLayerEvents(message, messages)

  return processResponses(data)
}

export const sendUserlike = async (
  value: string,
  group: string,
  {
    chatHistory,
    urlHistory,
    erxStatus,
    attachmentId,
    conversationId,
    contactInfo,
  }: UserlikePayload = {}
) => {
  const payload = {
    type: 'userlike',
    content: sanitizeUserMessage(value),
    chatHistory,
    urlHistory,
    erxStatus,
    attachmentId,
    conversationId,
    group,
    contactInfo,
  }

  // TODO add tracking events?

  return sendRequest({
    endpoint: 'webchat',
    payload,
  })
}

export const updateUserLikeData = async (
  refreshUuid: boolean,
  message?: string,
  lastUserId?: string
) => {
  const { conversationId } = loadSettingsItems()
  return sendRequest({
    endpoint: 'userlike-userdata',
    payload: {
      noAction: refreshUuid,
      message,
      lastUserId,
      conversationId,
    },
    refreshUuid,
  })
}

export const sendError = async (msg: string, extra = {}) => {
  const uaParser = new UAParser()
  const browser = uaParser.getBrowser()
  const device = uaParser.getDevice()
  const os = uaParser.getOS()

  const info = {
    browser: `${browser.name} ${browser.version}`,
    device: `${device.vendor} ${device.model}`,
    os: `${os.name} ${os.version}`,
    ...extra,
  }

  return sendRequest({
    endpoint: 'logError',
    payload: {
      msg,
      sentryTargetEnv: process.env.REACT_APP_ENV,
      info,
    },
  })
}

export const authorizeQuery = async (
  username: string,
  password: string,
  fcSolution: string
) => {
  const url = `${getLoginBaseUrl()}/homeone/api/login`

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username,
      password,
      fcSolution,
    }),
  })

  if (!res.ok) {
    const resBody = await res.json()
    const errorMessage =
      resBody.message ===
      'customer-authentication.error.wrong.username.or.password'
        ? t`Your e-mail address and/or password are incorrect. Please check your entries.`
        : t`An unexpected error has occurred, please try again later.`

    return {
      error: true,
      errorMessage,
    }
  }

  return {
    error: false,
  }
}

export const verifyUserQuery = async (): Promise<VerifyUserResponse> => {
  const verifyToken = process.env.REACT_APP_API_TOKEN || ''
  const tenant = getTenant()
  const language = getTenantLanguage()
  const url = `${getApiBaseUrl()}/me`

  return fetchJson(url, {
    headers: {
      'X-Verify-Token': verifyToken,
    },
    params: {
      tenant,
      language,
    },
  })
}

export const uploadFileRequest = async (file: File) => {
  const verifyToken = process.env.REACT_APP_API_TOKEN || ''

  const formData = new FormData()
  formData.append('file', file, file.name)

  const url = `${getApiBaseUrl()}/upload`

  return fetchJson(url, {
    method: 'POST',
    headers: {
      'X-Verify-Token': verifyToken,
    },
    body: formData,
  })
}

export const deleteFileRequest = async (fileName: string) => {
  const verifyToken = process.env.REACT_APP_API_TOKEN || ''
  const url = `${getApiBaseUrl()}/upload/${fileName}`

  return fetchJson(url, {
    method: 'DELETE',
    headers: {
      'X-Verify-Token': verifyToken,
    },
  })
}
